import React from 'react';
import { Service, User } from '../../types';
import { Clock, Check, X, ShoppingCart, CheckCircle, Sparkles, ChevronDown, List, Tag, HelpCircle, Search, Heart, Package, Trash2, Calendar, AlertTriangle, Store, MapPin } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { apiService } from '../../services/api';
import toast from 'react-hot-toast';
import { searchServices } from '../../utils/searchUtils';

interface ThemeStyles {
  pageBg: string;
  cardBg: string;
  textPrimary: string;
  textSecondary: string;
  border: string;
  buttonPrimary: string;
  buttonSecondary: string;
  buttonSuccess: string;
  progressActive: string;
  progressInactive: string;
  selected: string;
  unselected: string;
  success: string;
  error: string;
  inlineStyles: {
    pageBg: React.CSSProperties;
    cardBg: React.CSSProperties;
    textPrimary: React.CSSProperties;
    textSecondary: React.CSSProperties;
    border: React.CSSProperties;
    buttonPrimary: React.CSSProperties;
    buttonPrimaryHover: React.CSSProperties;
    buttonSecondary: React.CSSProperties;
    buttonSecondaryHover: React.CSSProperties;
    buttonSuccess: React.CSSProperties;
    progressActive: React.CSSProperties;
    progressInactive: React.CSSProperties;
    selected: React.CSSProperties;
    unselected: React.CSSProperties;
    success: React.CSSProperties;
    error: React.CSSProperties;
  };
}

interface BookingStep1Props {
  services: Service[];
  selectedServices: Service[];
  onServicesChange: (services: Service[]) => void;
  preselectedServiceId: string | null;
  hasRemovedPreselectedService?: boolean; // Track if user intentionally removed preselected service
  categories?: Array<{ id: string; name: string; icon?: string; display_order?: number }>;
  hideMobileCart?: boolean; // When true, hides the floating cart button + cart modal
  themeStyles?: ThemeStyles;
  isNeonTheme?: boolean;
  neonColors?: {
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
  };
  onNext?: () => void;
  canProceed?: boolean;
  audience?: 'customer' | 'professional'; // Audience for favorites (default: 'customer')
  businessId?: string; // Business ID for scoping favorites
  selectedProfessionals?: { [serviceId: string]: User | undefined }; // Selected professionals for each service
  selectedDate?: Date | null; // Selected appointment date
  selectedTime?: string; // Selected appointment time
}

const BookingStep1: React.FC<BookingStep1Props> = ({
  services,
  selectedServices,
  onServicesChange,
  preselectedServiceId,
  hasRemovedPreselectedService = false,
  categories: providedCategories,
  hideMobileCart = false,
  themeStyles,
  isNeonTheme = false,
  neonColors,
  onNext,
  canProceed = false,
  audience = 'customer',
  businessId,
  selectedProfessionals = {},
  selectedDate = null,
  selectedTime = ''
}) => {
  const [selectedCategory, setSelectedCategory] = React.useState<string>('all');
  const [showMobileCart, setShowMobileCart] = React.useState<boolean>(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = React.useState<boolean>(false);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const { user } = useAuth();
  const [favoriteServiceIds, setFavoriteServiceIds] = React.useState<Set<string>>(new Set());
  const [loadingFavorites, setLoadingFavorites] = React.useState<boolean>(!!user); // Start as true if user exists
  const [hasInitializedCategory, setHasInitializedCategory] = React.useState<boolean>(false);
  const [hasAutoAddedPreselected, setHasAutoAddedPreselected] = React.useState<boolean>(false);
  const [mobileServicesDisplayCount, setMobileServicesDisplayCount] = React.useState<number>(7);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  // Get primary color from theme or default
  const primaryColor = themeStyles?.inlineStyles?.buttonPrimary.backgroundColor || '#3B82F6';

  // Add-ons cannot be booked alone; require at least 1 non-add-on service to proceed.
  // Be defensive: some API payloads may use `is_add_on` instead of `isAddOn`.
  const hasAtLeastOneNonAddOnServiceSelected = selectedServices.some((s: any) => {
    const isAddOn = s?.isAddOn ?? s?.is_add_on;
    return isAddOn !== true;
  });
  const canProceedEffective = Boolean(canProceed) && hasAtLeastOneNonAddOnServiceSelected;

  // Auto-select pre-selected service if it exists and isn't already selected
  // Auto-add preselected service only once on initial load
  // IMPORTANT: Don't re-add if user has intentionally removed it
  React.useEffect(() => {
    if (preselectedServiceId && services.length > 0 && !hasAutoAddedPreselected && !hasRemovedPreselectedService) {
      const preselectedService = services.find(s => s.id === preselectedServiceId || s.id.toString() === preselectedServiceId);
      if (preselectedService && !selectedServices.some(s => s.id === preselectedService.id)) {
        onServicesChange([...selectedServices, preselectedService]);
        setHasAutoAddedPreselected(true);
        setTimeout(() => {
          const element = document.querySelector(`[data-service-id="${preselectedService.id}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else if (preselectedService && selectedServices.some(s => s.id === preselectedService.id)) {
        // If preselected service is already in selectedServices, mark as auto-added
        setHasAutoAddedPreselected(true);
      }
    }
  }, [preselectedServiceId, services, selectedServices, onServicesChange, hasAutoAddedPreselected, hasRemovedPreselectedService]);

  // Get businessId from services if not provided as prop (all services should have same businessId)
  const actualBusinessId = businessId || (services.length > 0 ? services[0].businessId : undefined);

  // Fetch favorite services when user is logged in
  React.useEffect(() => {
    const fetchFavorites = async () => {
      if (!user || !actualBusinessId) {
        setFavoriteServiceIds(new Set());
        setLoadingFavorites(false);
        setHasInitializedCategory(false); // Reset to allow initialization
        return;
      }

      try {
        setLoadingFavorites(true);
        setHasInitializedCategory(false); // Reset before loading
        const favorites = await apiService.getFavoriteServices(audience, actualBusinessId);
        const favoriteIds = new Set(favorites.map((f: Service) => f.id));
        setFavoriteServiceIds(favoriteIds);
      } catch (error) {
        console.error('Error fetching favorite services:', error);
        // Don't show error toast for unauthenticated users
        if (user) {
          toast.error('Failed to load favorite services');
        }
      } finally {
        setLoadingFavorites(false);
      }
    };

    fetchFavorites();
  }, [user, audience, actualBusinessId]);

  // Handle favorite toggle
  const handleFavoriteToggle = async (e: React.MouseEvent, serviceId: string) => {
    e.stopPropagation(); // Prevent service selection when clicking favorite button

    if (!user) {
      toast.error('Please sign in to save favorites');
      return;
    }

    const isCurrentlyFavorited = favoriteServiceIds.has(serviceId);
    const newFavoriteIds = new Set(favoriteServiceIds);

    // Optimistic update
    if (isCurrentlyFavorited) {
      newFavoriteIds.delete(serviceId);
    } else {
      newFavoriteIds.add(serviceId);
    }
    setFavoriteServiceIds(newFavoriteIds);

    // Get businessId from the service being favorited
    const service = services.find(s => s.id === serviceId);
    const serviceBusinessId = service?.businessId || actualBusinessId;

    try {
      if (isCurrentlyFavorited) {
        await apiService.removeFavoriteService(serviceId, audience, serviceBusinessId);
        toast.success('Removed from favorites');
      } else {
        await apiService.addFavoriteService(serviceId, audience, serviceBusinessId);
        toast.success('Added to favorites');
      }
    } catch (error: any) {
      // Revert optimistic update on error
      setFavoriteServiceIds(favoriteServiceIds);
      
      if (error.response?.status === 401) {
        toast.error('Please sign in to save favorites');
      } else if (error.response?.status === 409) {
        // Already favorited, just update the state
        setFavoriteServiceIds(newFavoriteIds);
      } else {
        toast.error(isCurrentlyFavorited ? 'Failed to remove from favorites' : 'Failed to add to favorites');
      }
    }
  };

  // Extract unique categories
  const getUniqueCategories = React.useCallback(() => {
    interface Cat { id: string; name: string; displayOrder: number; icon?: string; }
    const ORDER_FALLBACK = 999999;
    
    // Create a map of provided categories for quick lookup
    const categoryMap = new Map<string, { icon?: string; display_order?: number }>();
    if (providedCategories) {
      providedCategories.forEach(cat => {
        categoryMap.set(cat.id, { icon: cat.icon, display_order: cat.display_order });
      });
    }
    
    const categories = services.reduce((acc, svc) => {
      const s: any = svc;
      let catObj: any = undefined;
      if (s.category) catObj = s.category;
      else if (s.categoryId && s.categoryName) catObj = { id: s.categoryId, name: s.categoryName, displayOrder: s.categoryDisplayOrder, icon: s.categoryIcon };
      else if (s.category_id && s.category_name) catObj = { id: s.category_id, name: s.category_name, display_order: s.category_display_order, icon: s.category_icon };
      else if (typeof s.category === 'string') catObj = { id: s.category, name: s.category };

      if (!catObj) return acc;

      const id = catObj.id || catObj.category_id || catObj.categoryId;
      const name = catObj.name || catObj.category_name || catObj.categoryName || 'Unnamed';
      if (!id || !name) return acc;

      const rawOrder = catObj.displayOrder ?? catObj.display_order ?? catObj.displayorder ?? catObj.order ?? catObj.sequence ?? catObj.sortOrder;
      const displayOrder = Number.isFinite(Number(rawOrder)) ? Number(rawOrder) : ORDER_FALLBACK;
      
      // Get icon from provided categories if available, otherwise from service data
      const categoryInfo = categoryMap.get(id.toString());
      const icon = categoryInfo?.icon || catObj.icon || catObj.category_icon || catObj.categoryIcon;
      
      // Use display_order from provided categories if available
      const finalDisplayOrder = categoryInfo?.display_order !== undefined 
        ? Number(categoryInfo.display_order) 
        : displayOrder;

      if (!acc.some(c => c.id === id)) {
        acc.push({ id: id.toString(), name: String(name), displayOrder: finalDisplayOrder, icon });
      }
      return acc;
    }, [] as Cat[]);
    return categories.sort((a, b) => (a.displayOrder - b.displayOrder) || a.name.localeCompare(b.name));
  }, [services, providedCategories]);

  const categories = getUniqueCategories();
  
  // Add "My Favorites" category if user has favorites
  const hasFavorites = favoriteServiceIds.size > 0;
  const categoriesWithFavorites = hasFavorites 
    ? [
        { id: 'all', name: 'All Services', displayOrder: -1, icon: undefined },
        { id: 'my-favorites', name: 'My Favorites', displayOrder: 0, icon: '❤️' },
        ...categories
      ]
    : [
        { id: 'all', name: 'All Services', displayOrder: -1, icon: undefined },
        ...categories
      ];
  
  // Set default category selection based on preselected service or favorites
  React.useEffect(() => {
    // Only set default when favorites have finished loading, services are available, and we haven't initialized yet
    // Also check if we're still on 'all' to avoid overriding user selection
    if (!loadingFavorites && !hasInitializedCategory && selectedCategory === 'all' && categoriesWithFavorites.length > 1 && services.length > 0) {
      // If there's a preselected service, determine which category to select
      if (preselectedServiceId) {
        const preselectedService = services.find(s => s.id === preselectedServiceId || s.id.toString() === preselectedServiceId);
        if (preselectedService) {
          // Check if preselected service is in favorites
          if (favoriteServiceIds.has(preselectedService.id)) {
            setSelectedCategory('my-favorites');
          } else {
            // Find the category of the preselected service
            const serviceCategory = preselectedService.category;
            if (serviceCategory) {
              // Handle different category property formats
              const serviceCategoryAny = serviceCategory as any;
              const categoryId = serviceCategoryAny.id || serviceCategoryAny.category_id || serviceCategoryAny.categoryId;
              if (categoryId) {
                // Check if this category exists in categoriesWithFavorites
                const categoryExists = categoriesWithFavorites.find(c => c.id === categoryId.toString());
                if (categoryExists) {
                  setSelectedCategory(categoryId.toString());
                } else {
                  // Category not found, fall back to first category or favorites
                  setSelectedCategory(hasFavorites ? 'my-favorites' : categoriesWithFavorites[1].id);
                }
              } else {
                // No category, fall back to first category or favorites
                setSelectedCategory(hasFavorites ? 'my-favorites' : categoriesWithFavorites[1].id);
              }
            } else {
              // No category, fall back to first category or favorites
              setSelectedCategory(hasFavorites ? 'my-favorites' : categoriesWithFavorites[1].id);
            }
          }
        } else {
          // Preselected service not found, use default logic
          setSelectedCategory(hasFavorites ? 'my-favorites' : categoriesWithFavorites[1].id);
        }
      } else {
        // No preselected service, use default logic
        if (hasFavorites) {
          setSelectedCategory('my-favorites');
        } else {
          // Select first category after "All Services"
          setSelectedCategory(categoriesWithFavorites[1].id);
        }
      }
      setHasInitializedCategory(true);
    }
  }, [loadingFavorites, hasFavorites, categoriesWithFavorites.length, hasInitializedCategory, selectedCategory, preselectedServiceId, services, favoriteServiceIds]);

  // Auto-switch to "All Services" if "My Favorites" is selected but there are no favorites
  React.useEffect(() => {
    if (selectedCategory === 'my-favorites' && favoriteServiceIds.size === 0 && !loadingFavorites) {
      setSelectedCategory('all');
    }
  }, [selectedCategory, favoriteServiceIds.size, loadingFavorites]);

  // Reset mobile services display count when category or search changes
  React.useEffect(() => {
    setMobileServicesDisplayCount(7);
  }, [selectedCategory, searchQuery]);

  // Format price helper
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(price);
  };

  const getServicePriceText = (service: Service): string => {
    const s: any = service;
    if (s?.quotable === true) return 'Request a Quote';
    return formatPrice(Number(s?.price ?? 0));
  };

  // Filter services by selected category and search query
  const filteredServices = React.useMemo(() => {
    let filtered = selectedCategory === 'all'
      ? services
      : selectedCategory === 'my-favorites'
      ? services.filter(service => favoriteServiceIds.has(service.id))
      : services.filter(service => {
          const cat: any = service.category;
          const catId = cat?.id || cat?.category_id || cat?.categoryId;
          return catId?.toString() === selectedCategory;
        });

    // Apply fuzzy search filter if query exists
    if (searchQuery.trim()) {
      filtered = searchServices(filtered, searchQuery);
    }
    
    // Sort: add-ons go to the end
    return filtered.sort((a, b) => {
      const aIsAddOn = (a as any).isAddOn || (a as any).is_add_on;
      const bIsAddOn = (b as any).isAddOn || (b as any).is_add_on;
      
      if (aIsAddOn && !bIsAddOn) return 1; // a is add-on, b is not - a goes last
      if (!aIsAddOn && bIsAddOn) return -1; // a is not add-on, b is - b goes last
      return 0; // Both same type, maintain original order
    });
  }, [services, selectedCategory, searchQuery, favoriteServiceIds]);

  const handleServiceToggle = (service: Service) => {
    const isSelected = selectedServices.some(s => s.id === service.id);
    if (isSelected) {
      onServicesChange(selectedServices.filter(s => s.id !== service.id));
    } else {
      onServicesChange([...selectedServices, service]);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  const getServiceCategoryName = (service: Service): string | null => {
    const s: any = service;
    const cat: any = s.category;

    // Try populated object first
    const categoryId = cat?.id || cat?.category_id || cat?.categoryId || s.categoryId || s.category_id;
    const categoryName =
      cat?.name ||
      cat?.category_name ||
      cat?.categoryName ||
      s.categoryName ||
      s.category_name;

    if (categoryName && typeof categoryName === 'string') return categoryName;

    // Fall back to provided categories list (if we have an id)
    if (categoryId && providedCategories?.length) {
      const match = providedCategories.find(c => c.id.toString() === categoryId.toString());
      if (match?.name) return match.name;
    }

    // If category is just a string (some backends), use it
    if (typeof cat === 'string' && cat.trim()) return cat;

    return null;
  };

  const getServiceIcon = (service: Service): string | null => {
    const s: any = service;
    
    // 1. If service has an icon, use it
    const serviceIcon = s.icon || service.icon;
    if (serviceIcon && typeof serviceIcon === 'string' && serviceIcon.trim() !== '') {
      return serviceIcon;
    }

    // 2. If no service icon, try to get category icon
    const cat: any = s.category;
    const categoryId = cat?.id || cat?.category_id || cat?.categoryId || s.categoryId || s.category_id;
    
    // Try to get icon from category object on service
    const categoryIcon = cat?.icon || cat?.category_icon || cat?.categoryIcon || s.categoryIcon || s.category_icon;
    if (categoryIcon && typeof categoryIcon === 'string' && categoryIcon.trim() !== '') {
      return categoryIcon;
    }

    // Try to get icon from provided categories list
    if (categoryId && providedCategories?.length) {
      const match = providedCategories.find(c => c.id.toString() === categoryId.toString());
      if (match?.icon && typeof match.icon === 'string' && match.icon.trim() !== '') {
        return match.icon;
      }
    }

    // Try to get icon from categories list (extracted from services)
    if (categoryId) {
      const match = categories.find(c => c.id.toString() === categoryId.toString());
      if (match?.icon && typeof match.icon === 'string' && match.icon.trim() !== '') {
        return match.icon;
      }
    }

    // 3. No icon found, return null (will use star icon as fallback)
    return null;
  };

  const handleRemoveService = (serviceId: string | number) => {
    const updatedServices = selectedServices.filter(s => s.id !== serviceId);
    onServicesChange(updatedServices);
  };

  const renderSelectedServices = (isMobile: boolean = false) => {
    const totalPrice = selectedServices.reduce((sum, service) => sum + Number((service as any)?.price ?? 0), 0);
    const hasQuotable = selectedServices.some((s: any) => s?.quotable === true);

    return (
      <div 
        className={`bg-white rounded-xl shadow-lg border border-gray-200 ${isMobile ? '' : 'lg:flex lg:flex-col lg:h-full'}`}
        style={{
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
        <div className={`p-5 ${isMobile ? '' : 'lg:flex lg:flex-col lg:flex-1 lg:p-6'}`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div 
                className="w-11 h-11 rounded-xl flex items-center justify-center shadow-sm"
                style={{ 
                  backgroundColor: `${primaryColor}10`,
                  border: `1px solid ${primaryColor}20`
                }}
              >
                <Package className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 leading-tight">Selected Services</h3>
                <p className="text-xs text-gray-500 mt-0.5">{selectedServices.length} {selectedServices.length === 1 ? 'service' : 'services'}</p>
              </div>
            </div>
            {isMobile && (
              <button
                onClick={() => setShowMobileCart(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-100 rounded-lg"
                aria-label="Close cart"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Services List */}
          <div className={`${isMobile ? 'mb-5 max-h-[60vh] overflow-y-auto' : 'lg:mb-5 lg:flex-1 lg:overflow-y-auto'}`}>
            {selectedServices.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-gray-100">
                  <ShoppingCart className="w-7 h-7 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-600 mb-1">No services selected</p>
                <p className="text-xs text-gray-400">Select services from the list to add them here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {selectedServices.map((service) => {
                  const categoryName = getServiceCategoryName(service);
                  const icon = getServiceIcon(service);
                  
                  return (
                    <div
                      key={service.id}
                      className="group bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 hover:shadow-sm transition-all duration-200"
                    >
                      <div className="flex items-start gap-3">
                        {/* Service Icon */}
                        <div className="flex-shrink-0">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${primaryColor}08` }}
                          >
                            {icon ? (
                              <span className="text-lg">{icon}</span>
                            ) : (
                              <Package className="w-5 h-5" style={{ color: primaryColor }} />
                            )}
                          </div>
                        </div>

                        {/* Service Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 leading-tight">
                                {service.name}
                              </p>
                              {categoryName && (
                                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                  <Tag className="w-3 h-3" />
                                  <span className="truncate">{categoryName}</span>
                                </p>
                              )}
                              {/* Professional Selection Status */}
                              {(() => {
                                const professional = selectedProfessionals[service.id];
                                if (professional) {
                                  // Show professional name if selected
                                  const professionalName = professional.firstName && professional.lastName
                                    ? `${professional.firstName} ${professional.lastName}`
                                    : professional.email || 'Professional';
                                  return (
                                    <p className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                                      <span className="font-medium">{professionalName}</span>
                                    </p>
                                  );
                                } else {
                                  // Show warning if no professional selected
                                  return (
                                    <p className="text-xs text-orange-600 mt-1 flex items-center gap-1 font-medium">
                                      <AlertTriangle className="w-3 h-3" />
                                      <span>Professional is required</span>
                                    </p>
                                  );
                                }
                              })()}
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <span className="text-sm font-bold text-gray-900 tabular-nums whitespace-nowrap">
                                {getServicePriceText(service)}
                              </span>
                              {/* Remove Button - Always visible with red X icon */}
                              <button
                                onClick={() => handleRemoveService(service.id)}
                                className="flex-shrink-0 text-red-600 hover:text-red-700 transition-all p-1.5 hover:bg-red-50 rounded-lg"
                                aria-label={`Remove ${service.name}`}
                                type="button"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          {/* Duration */}
                          <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-500">
                            <Clock className="w-3 h-3" />
                            <span>{formatDuration(service.durationMinutes)}</span>
                          </div>
                          
                          {/* Appointment Date/Time if selected */}
                          {selectedDate && selectedTime && (
                            <div className="flex items-center gap-1.5 mt-2 text-xs text-gray-600">
                              <Calendar className="w-3 h-3" />
                              <span>
                                {selectedDate.toLocaleDateString('en-US', { 
                                  weekday: 'short', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })} at {selectedTime}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer with Total and Continue Button */}
          {selectedServices.length > 0 && (
            <div className="border-t border-gray-200 pt-5 mt-5">
              <div className="flex justify-between items-center mb-4 px-1">
                <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Total</span>
                <span className="text-xl font-bold tabular-nums" style={{ color: primaryColor }}>
                  {hasQuotable ? 'Quote required' : formatPrice(totalPrice)}
                </span>
              </div>
              {onNext && (
                <button
                  onClick={() => canProceedEffective && onNext()}
                  disabled={!canProceedEffective}
                  className={`w-full px-5 py-3 text-sm font-semibold rounded-lg transition-all ${
                    !canProceedEffective 
                      ? 'opacity-50 cursor-not-allowed bg-gray-300 text-gray-500' 
                      : 'hover:shadow-md hover:scale-[1.02] active:scale-[0.98] text-white shadow-sm'
                  }`}
                  style={{ 
                    backgroundColor: !canProceedEffective ? undefined : primaryColor
                  }}
                >
                  Continue to Next Step
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <Sparkles className="w-4 h-4" style={{ color: primaryColor }} />
          </div>
          <h2 className="text-xl text-gray-900">Select Services</h2>
          <div className="relative group flex-shrink-0">
            <HelpCircle className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none w-64">
              <div className="bg-gray-900 text-white text-sm rounded-lg py-2 px-3 shadow-lg">
                <div className="whitespace-normal text-left">Choose the services you'd like to book. You can select multiple services.</div>
                <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Search Bar - Desktop Only */}
        <div className="hidden lg:block mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search services by name, price, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none text-sm transition-all"
              style={{ 
                '--tw-ring-color': primaryColor,
              } as React.CSSProperties & { '--tw-ring-color': string }}
              onFocus={(e) => {
                e.target.style.borderColor = primaryColor;
                e.target.style.boxShadow = `0 0 0 2px ${primaryColor}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.boxShadow = '';
              }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-stretch lg:gap-8">
        {/* Category Filter - Mobile Dropdown / Desktop Vertical Column */}
        {services.length > 0 && categoriesWithFavorites.length > 1 && (
          <>
            {/* Mobile/Tablet Dropdown */}
            <div className="lg:hidden mb-6">
              <div className="flex flex-col gap-2">
                <div className="relative w-full" ref={dropdownRef}>
                  <button
                    onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    className="w-full px-5 py-2.5 text-sm font-medium rounded-md transition-opacity hover:opacity-90 flex items-center justify-between bg-white border border-gray-300 text-gray-700 h-10"
                    style={selectedCategory !== 'all' ? { borderColor: primaryColor } : {}}
                  >
                    <div className="flex items-center gap-2">
                      {(() => {
                        const selectedCat = categoriesWithFavorites.find(c => c.id === selectedCategory || c.name === selectedCategory);
                        if (selectedCategory === 'all') {
                          return <List className="w-4 h-4" />;
                        } else if (selectedCat?.icon) {
                          return <span className="text-base">{selectedCat.icon}</span>;
                        } else {
                          return <Tag className="w-4 h-4" />;
                        }
                      })()}
                    <span>
                      {(() => {
                        const selectedCat = categoriesWithFavorites.find(c => c.id === selectedCategory || c.name === selectedCategory);
                        return selectedCat?.name || 'All Services';
                      })()}
                    </span>
                    </div>
                    <ChevronDown 
                      className={`w-4 h-4 transition-transform ${isCategoryDropdownOpen ? 'transform rotate-180' : ''}`}
                    />
                  </button>
                  {isCategoryDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                      <div
                        className={`px-4 py-2.5 text-sm font-medium cursor-pointer flex items-center gap-2 ${
                          selectedCategory === 'all'
                            ? 'text-white'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                        style={selectedCategory === 'all' ? { backgroundColor: primaryColor } : {}}
                        onClick={() => {
                          setSelectedCategory('all');
                          setIsCategoryDropdownOpen(false);
                        }}
                      >
                        <List className="w-4 h-4" />
                        <span>All Services</span>
                      </div>
                      {categoriesWithFavorites.slice(1).map((category) => (
                        <div
                          key={category.id}
                          className={`px-4 py-2.5 text-sm font-medium cursor-pointer flex items-center gap-2 ${
                            selectedCategory === category.id
                              ? 'text-white'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                          style={selectedCategory === category.id ? { backgroundColor: primaryColor } : {}}
                          onClick={() => {
                            setSelectedCategory(category.id);
                            setIsCategoryDropdownOpen(false);
                          }}
                        >
                          {category.icon ? (
                            <span className="text-base">{category.icon}</span>
                          ) : (
                            <Tag className="w-4 h-4" />
                          )}
                          <span>{category.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Search Bar - Mobile */}
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search services..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-md focus:outline-none text-sm font-medium bg-white text-gray-700 transition-all h-10"
                    style={{ 
                      '--tw-ring-color': primaryColor,
                    } as React.CSSProperties & { '--tw-ring-color': string }}
                    onFocus={(e) => {
                      e.target.style.borderColor = primaryColor;
                      e.target.style.boxShadow = `0 0 0 2px ${primaryColor}20`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#D1D5DB';
                      e.target.style.boxShadow = '';
                    }}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      type="button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop Vertical Column */}
            <div className="hidden lg:flex lg:flex-col lg:w-48 lg:flex-shrink-0 lg:gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center gap-2 w-full ${
                  selectedCategory === 'all'
                    ? 'text-white shadow-sm'
                    : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                }`}
                style={selectedCategory === 'all' ? { backgroundColor: primaryColor } : {}}
              >
                <List className="w-4 h-4 flex-shrink-0" />
                <span>All Services</span>
              </button>
              {categoriesWithFavorites.slice(1).map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2.5 text-sm font-medium rounded-lg transition-all flex items-center gap-2 w-full ${
                    selectedCategory === category.id
                      ? 'text-white shadow-sm'
                      : 'text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                  }`}
                  style={selectedCategory === category.id ? { backgroundColor: primaryColor } : {}}
                >
                  {category.icon ? (
                    <span className="text-base flex items-center justify-center flex-shrink-0">{category.icon}</span>
                  ) : (
                    <Tag className="w-4 h-4 flex-shrink-0" />
                  )}
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="flex-1 space-y-6">
           {/* Services Grid */}
           <div className="lg:pr-0">
          {services.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No services available at the moment.</p>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No services found in this category.</p>
            </div>
          ) : (
            <>
              {/* Mobile: Service count divider */}
              <div className="lg:hidden mb-3 pt-0.5 pb-2 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded-md flex items-center justify-center"
                      style={{ backgroundColor: `${primaryColor}15` }}
                    >
                      <List className="w-3.5 h-3.5" style={{ color: primaryColor }} />
                    </div>
                    <span className="text-sm font-semibold text-gray-700">
                      {filteredServices.length} {filteredServices.length === 1 ? 'Service' : 'Services'}
                    </span>
                  </div>
                  {searchQuery && (
                    <span className="text-xs text-gray-500">
                      Matching "{searchQuery}"
                    </span>
                  )}
                </div>
              </div>
              
              {/* Mobile: Limited services */}
              <div className="lg:hidden grid grid-cols-1 gap-4 md:gap-6">
                {filteredServices.slice(0, mobileServicesDisplayCount).map((service) => {
                  const isSelected = selectedServices.some(s => s.id === service.id);
                  const isPreselected = preselectedServiceId && (
                    preselectedServiceId === service.id || 
                    preselectedServiceId === service.id.toString()
                  );

                  const isFavorited = favoriteServiceIds.has(service.id);

                  return (
                    <div
                      key={service.id}
                      data-service-id={service.id}
                      onClick={() => handleServiceToggle(service)}
                      className={`border rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer px-3 pt-3 pb-3 flex flex-col overflow-visible relative ${
                        isSelected ? 'border-2' : 'border-gray-200 bg-white'
                      }`}
                      style={isSelected ? { 
                        borderColor: primaryColor,
                        backgroundColor: `${primaryColor}10`
                      } : {}}
                    >
                      {/* Favorite Button - Only show when user is logged in */}
                      {user && (
                        <button
                          onClick={(e) => handleFavoriteToggle(e, service.id)}
                          className={`absolute top-2 right-2 p-1.5 rounded-full transition-all z-10 ${
                            isFavorited 
                              ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                              : 'text-gray-400 hover:text-red-500 bg-white hover:bg-gray-50'
                          }`}
                          aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                          title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Heart 
                            className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`}
                          />
                        </button>
                      )}

                      {/* Service Icon and Name */}
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0">
                          <div 
                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ backgroundColor: `${primaryColor}15` }}
                          >
                            {(() => {
                              const icon = getServiceIcon(service);
                              return icon ? (
                                <span className="text-xl" style={{ color: primaryColor }}>
                                  {icon}
                                </span>
                              ) : (
                                <i 
                                  className="bi bi-star-fill"
                                  style={{ 
                                    fontSize: '20px',
                                    color: '#FBBF24'
                                  }}
                                ></i>
                              );
                            })()}
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1.5 truncate">
                            {service.name}
                          </h3>
                          {/* Duration and Price under service name */}
                          <div className="flex items-center space-x-2 text-xs text-gray-600">
                            <div className="flex items-center space-x-1 flex-shrink-0">
                              <Clock className="w-3 h-3 flex-shrink-0" />
                              <span className="whitespace-nowrap">{formatDuration(service.durationMinutes)}</span>
                            </div>
                            <span className="font-semibold whitespace-nowrap flex-shrink-0" style={{ color: primaryColor }}>
                              {getServicePriceText(service)}
                            </span>
                            {/* Add-on Badge - Next to Price */}
                            {((service as any).isAddOn || (service as any).is_add_on) && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm ml-1">
                                Add-on
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Service Location Icon - Bottom Right */}
                      {(() => {
                        const rawServiceLocation = service.serviceLocation || (service as any).service_location;
                        const serviceLocation = rawServiceLocation 
                          ? (typeof rawServiceLocation === 'string' ? rawServiceLocation.toUpperCase().trim() : 'IN_STORE')
                          : 'IN_STORE';
                        
                        if (serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' || serviceLocation === 'ON_SITE' || serviceLocation === 'ONSITE') {
                          const tooltipText = serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? 'In-store service' : 'On-site service';
                          return (
                            <div className="absolute bottom-2 right-2 group">
                              <div className="p-1.5 text-gray-400 cursor-help">
                                {serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? (
                                  <Store className="w-4 h-4" />
                                ) : (
                                  <MapPin className="w-4 h-4" />
                                )}
                              </div>
                              {/* Tooltip */}
                              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none whitespace-nowrap">
                                <div className="bg-gray-900 text-white text-xs rounded-lg py-1.5 px-2 shadow-lg">
                                  {tooltipText}
                                  <div className="absolute right-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  );
                })}
              </div>
              
              {/* Desktop: All services */}
              <div className="hidden lg:grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                {filteredServices.map((service) => {
                const isSelected = selectedServices.some(s => s.id === service.id);
                const isPreselected = preselectedServiceId && (
                  preselectedServiceId === service.id || 
                  preselectedServiceId === service.id.toString()
                );

                const isFavorited = favoriteServiceIds.has(service.id);

                return (
                  <div
                    key={service.id}
                    data-service-id={service.id}
                    onClick={() => handleServiceToggle(service)}
                    className={`border rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer px-3 pt-3 pb-3 flex flex-col overflow-hidden relative ${
                      isSelected ? 'border-2' : 'border-gray-200 bg-white'
                    }`}
                    style={isSelected ? { 
                      borderColor: primaryColor,
                      backgroundColor: `${primaryColor}10`
                    } : {}}
                  >
                    {/* Favorite Button - Only show when user is logged in */}
                    {user && (
                      <button
                        onClick={(e) => handleFavoriteToggle(e, service.id)}
                        className={`absolute top-2 right-2 p-1.5 rounded-full transition-all z-10 ${
                          isFavorited 
                            ? 'text-red-500 hover:text-red-600 bg-red-50 hover:bg-red-100' 
                            : 'text-gray-400 hover:text-red-500 bg-white hover:bg-gray-50'
                        }`}
                        aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                        title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Heart 
                          className={`w-4 h-4 ${isFavorited ? 'fill-current' : ''}`}
                        />
                      </button>
                    )}

                    {/* Service Icon and Name */}
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${primaryColor}15` }}
                        >
                          {(() => {
                            const icon = getServiceIcon(service);
                            return icon ? (
                              <span className="text-xl" style={{ color: primaryColor }}>
                                {icon}
                              </span>
                            ) : (
                              <i 
                                className="bi bi-star-fill"
                                style={{ 
                                  fontSize: '20px',
                                  color: '#FBBF24'
                                }}
                              ></i>
                            );
                          })()}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className="text-sm font-semibold text-gray-900 mb-1.5 truncate">
                          {service.name}
                        </h3>
                        {/* Duration and Price under service name */}
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          <div className="flex items-center space-x-1 flex-shrink-0">
                            <Clock className="w-3 h-3 flex-shrink-0" />
                            <span className="whitespace-nowrap">{formatDuration(service.durationMinutes)}</span>
                          </div>
                          <span className="font-semibold whitespace-nowrap flex-shrink-0" style={{ color: primaryColor }}>
                            {getServicePriceText(service)}
                          </span>
                          {/* Add-on Badge - Next to Price */}
                          {((service as any).isAddOn || (service as any).is_add_on) && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-sm ml-1">
                              Add-on
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Service Location Icon - Bottom Right */}
                      {(() => {
                        const rawServiceLocation = service.serviceLocation || (service as any).service_location;
                        const serviceLocation = rawServiceLocation 
                          ? (typeof rawServiceLocation === 'string' ? rawServiceLocation.toUpperCase().trim() : 'IN_STORE')
                          : 'IN_STORE';
                        
                        if (serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' || serviceLocation === 'ON_SITE' || serviceLocation === 'ONSITE') {
                          const tooltipText = serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? 'In-store service' : 'On-site service';
                          return (
                            <div className="absolute bottom-2 right-2 group">
                              <div className="p-1.5 text-gray-400 cursor-help">
                                {serviceLocation === 'IN_STORE' || serviceLocation === 'INSTORE' ? (
                                  <Store className="w-4 h-4" />
                                ) : (
                                  <MapPin className="w-4 h-4" />
                                )}
                              </div>
                              {/* Tooltip */}
                              <div className="absolute right-0 bottom-full mb-2 hidden group-hover:block z-50 pointer-events-none whitespace-nowrap">
                                <div className="bg-gray-900 text-white text-xs rounded-lg py-1.5 px-2 shadow-lg">
                                  {tooltipText}
                                  <div className="absolute right-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                );
              })}
              </div>
              
              {/* Load More Button - Mobile Only */}
              {filteredServices.length > mobileServicesDisplayCount && (
                <div className="lg:hidden mt-6">
                  <button
                    onClick={() => setMobileServicesDisplayCount(prev => prev + 7)}
                    className="w-full px-6 py-3 text-sm font-medium rounded-lg transition-opacity hover:opacity-90 text-white shadow-lg"
                    style={{ 
                      backgroundColor: primaryColor,
                      borderColor: primaryColor
                    }}
                  >
                    Load More...
                  </button>
                </div>
              )}
            </>
          )}
          </div>
        </div>

        {/* Floating Cart Button - Desktop and Mobile */}
        {!hideMobileCart && (selectedServices.length > 0) && (
          <>
            {!showMobileCart && (
              <>
                {/* Mobile Cart Button */}
                <button
                  onClick={() => setShowMobileCart(true)}
                  className="lg:hidden fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full shadow-lg p-4 transition-opacity hover:opacity-90 text-white"
                  style={{ backgroundColor: primaryColor }}
                  aria-label="View selected services"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span className="text-sm font-semibold px-2 py-1 rounded-full bg-white" style={{ color: primaryColor }}>
                    {selectedServices.length}
                  </span>
                </button>
                
                {/* Desktop Cart Button */}
                <button
                  onClick={() => setShowMobileCart(true)}
                  className="hidden lg:flex fixed bottom-6 right-6 z-40 items-center gap-2 rounded-full shadow-lg p-4 transition-opacity hover:opacity-90 text-white"
                  style={{ backgroundColor: primaryColor }}
                  aria-label="View selected services"
                >
                  <ShoppingCart className="w-5 h-5" />
                  <span className="text-sm font-semibold px-2 py-1 rounded-full bg-white" style={{ color: primaryColor }}>
                    {selectedServices.length}
                  </span>
                </button>
              </>
            )}
            {showMobileCart && (
              <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4" onClick={() => setShowMobileCart(false)}>
                <div className="bg-white rounded-lg shadow-xl max-h-[80vh] w-full max-w-md overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                  {renderSelectedServices(true)}
                </div>
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default BookingStep1;
